import { GroupIcon, MenuIcon, UserCircleIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Button } from "~/components/ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from "~/components/ui/dropdown-menu"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"

export function FilterSettings({
	onChangeGrouping,
	value,
	disableAssigneeGrouping,
	mobile,
	scope,
	onChangeScope,
	scopes,
	actions
}: {
	onChangeGrouping?: (
		value: "status" | "priority" | "assignee" | "difficulty"
	) => void
	value?: "status" | "priority" | "assignee" | "difficulty"
	disableAssigneeGrouping?: boolean
	mobile?: boolean
	scope?: string
	onChangeScope?: (scope: string) => void
	scopes?: { id: string; label: string; count: number }[]
	actions?: ReactNode
}) {
	const groupings = [
		{
			id: "status",
			name: "Status",
			icon: <StatusIcon status="ToDo" className="size-4" />
		},
		{
			id: "priority",
			name: "Priority",
			icon: <PriorityIcon priority="None" className="size-4" />
		},
		{
			id: "difficulty",
			name: "Difficulty",
			icon: <DifficultyIcon difficulty={1} className="size-4" />
		},
		disableAssigneeGrouping
			? undefined
			: {
					id: "assignee",
					name: "Assignee",
					icon: <UserCircleIcon className="size-4" />
				}
	] as const

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size={mobile ? "icon" : "sm"}
					aria-label={mobile ? "Issue filters and grouping" : "Group issues"}
				>
					{mobile ? (
						<MenuIcon className="size-4" />
					) : (
						<GroupIcon className="size-4 text-icons" />
					)}
					{!mobile && (
						<span className="hidden sm:inline">
							Group:{" "}
							{groupings.find((group) => group?.id === value)?.name ?? "Status"}
						</span>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				{mobile && actions && (
					<>
						{actions}
						<DropdownMenuSeparator />
					</>
				)}
				{mobile && (
					<>
						<DropdownMenuLabel>Filter</DropdownMenuLabel>
						<DropdownMenuRadioGroup value={scope} onValueChange={onChangeScope}>
							{scopes?.map((item) => (
								<DropdownMenuRadioItem key={item.id} value={item.id}>
									<span className="flex flex-1 items-center justify-between gap-4">
										{item.label}
										<span className="text-text-small">{item.count}</span>
									</span>
								</DropdownMenuRadioItem>
							))}
						</DropdownMenuRadioGroup>
						<DropdownMenuSeparator />
					</>
				)}

				{mobile && <DropdownMenuLabel>Group by</DropdownMenuLabel>}
				<DropdownMenuRadioGroup
					value={value}
					onValueChange={(value) =>
						onChangeGrouping?.(
							value as "status" | "priority" | "assignee" | "difficulty"
						)
					}
				>
					{groupings
						.filter((group) => !!group)
						.map((group) => (
							<DropdownMenuRadioItem
								key={group.id}
								value={group.id}
								className="gap-2"
							>
								{group.icon}
								<span>{group.name}</span>
							</DropdownMenuRadioItem>
						))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
